import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  createPendingMessage,
  hardResetFromMessage,
  setActivePointer,
} from '@/services/floorplanService';
import type {
  FloorplanVersion,
  FloorplanMessage,
  BoxCoordinates,
} from '@/types';
import type { IntentClassification } from '@/types/floorplanClassification';

interface UseFeintuningThreadArgs {
  imageId: string;
  activeVersion: FloorplanVersion | null;
  initialMessages: FloorplanMessage[];
  /**
   * Image the next edit is applied to — must reflect the user's active pointer
   * (Ausgangs-Stand or a specific message), not just the tail of the chain.
   * Parent computes this so the canvas preview and the OpenAI edit always
   * agree on which state the edit starts from.
   */
  currentStatePath: string | null;
  sourceStoragePath: string;
  projectId: string;
  userId: string;
  onResult?: (updatedMessage: FloorplanMessage) => void;
}

interface SendMessageArgs {
  prompt: string;
  box: BoxCoordinates | null;
  /**
   * Admin prompt-inspector escape hatch (Task 8): when set, the edge function
   * skips prompt assembly and sends this exact text to the image model.
   * Only ever set by FloorplanRefineInspectorPanel (admin-gated) — the
   * regular chat send path never passes this.
   */
  finalPromptOverride?: string;
}

export function useFloorplanFeintuningThread(args: UseFeintuningThreadArgs) {
  const [messages, setMessages] = useState<FloorplanMessage[]>(args.initialMessages);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rebuildSuggestion, setRebuildSuggestion] = useState<IntentClassification | null>(null);
  const [uncertainHint, setUncertainHint] = useState<IntentClassification | null>(null);
  const [lastSendArgs, setLastSendArgs] = useState<SendMessageArgs | null>(null);

  // Reset the message buffer when the active version changes — otherwise
  // `useState(initial)` only runs once and Version 2 would keep displaying
  // Version 1's chat history after a version switch.
  const activeVersionId = args.activeVersion?.id ?? null;
  useEffect(() => {
    setMessages(args.initialMessages);
    setError(null);
    // Intentionally not depending on initialMessages — in-version optimistic
    // updates should NOT be overwritten by parent refreshes of the same version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersionId]);

  const currentStatePath = args.currentStatePath;

  // Capture latest args + currentStatePath in refs so async callbacks always
  // read the freshest value at call-time, not at closure-creation time.
  // Phase 2 made parent re-renders frequent enough that stale closures became
  // observable: if the user clicked a chat variant (Edit 2) and immediately
  // sent a new message, an in-flight render between click and send could leave
  // sendMessage's closure pointing at the previous variant's path.
  const argsRef = useRef(args);
  argsRef.current = args;
  const currentStatePathRef = useRef(currentStatePath);
  currentStatePathRef.current = currentStatePath;

  const sendMessage = useCallback(
    async ({ prompt, box, finalPromptOverride }: SendMessageArgs): Promise<FloorplanMessage> => {
      // Always read from refs — guarantees we see the latest parent state at
      // call-time, eliminating closure-staleness bugs (Phase 2 regression:
      // user-active variant could be lost between render and send).
      const liveArgs = argsRef.current;
      const liveStatePath = currentStatePathRef.current;

      if (!liveArgs.activeVersion) {
        throw new Error('No active version — cannot send message');
      }
      if (!liveStatePath) {
        throw new Error('No current state path — cannot send message');
      }
      setIsGenerating(true);
      setError(null);

      // 1. INSERT pending in DB (and locally)
      const pendingMsg = await createPendingMessage(liveArgs.activeVersion.id, prompt, box);
      setMessages((prev) => [...prev, pendingMsg]);

      // Diagnostic log to verify parent → hook state plumbing at send-time.
      console.info('[feintuning-thread] invoking edge function with', {
        versionId: liveArgs.activeVersion.id,
        versionNumber: liveArgs.activeVersion.versionNumber,
        messageId: pendingMsg.id,
        messageNumber: pendingMsg.messageNumber,
        currentStatePath: liveStatePath,
      });

      try {
        const { data, error: fnErr } = await supabase.functions.invoke(
          'generate-floorplan-refinement',
          {
            body: {
              imageId: liveArgs.imageId,
              versionId: liveArgs.activeVersion.id,
              messageId: pendingMsg.id,
              userPrompt: prompt,
              boxCoordinates: box ?? undefined,
              currentStatePath: liveStatePath,
              sourceStoragePath: liveArgs.sourceStoragePath,
              projectId: liveArgs.projectId,
              userId: liveArgs.userId,
              finalPromptOverride,
            },
          }
        );

        // supabase-js wraps non-2xx as fnErr but still exposes the response body
        // via `error.context`. Prefer the body's errorMessage for a meaningful UI error.
        if (fnErr) {
          let detailedMsg = fnErr.message ?? 'Edge function error';
          try {
            const ctx = (fnErr as unknown as { context?: Response }).context;
            if (ctx && typeof ctx.json === 'function') {
              const body = await ctx.json();
              if (body?.errorMessage) detailedMsg = body.errorMessage;
              else if (body?.error) detailedMsg = typeof body.error === 'string' ? body.error : JSON.stringify(body.error);
            }
          } catch { /* body not JSON or already consumed */ }
          throw new Error(detailedMsg);
        }

        // Phase 2: handle new error codes — these are user-decision states, not failures.
        if (data?.success === false && data.errorCode === 'rebuild_suggested') {
          setRebuildSuggestion(data.classification as IntentClassification);
          setMessages((prev) =>
            prev.map((m) => m.id === pendingMsg.id ? { ...m, status: 'awaiting_user_action' as const } : m),
          );
          setLastSendArgs({ prompt, box });
          return pendingMsg;
        }

        if (data?.success === false && data.errorCode === 'classification_uncertain') {
          setUncertainHint(data.classification as IntentClassification);
          setMessages((prev) =>
            prev.map((m) => m.id === pendingMsg.id ? { ...m, status: 'awaiting_user_action' as const } : m),
          );
          setLastSendArgs({ prompt, box });
          return pendingMsg;
        }

        if (!data?.success) {
          throw new Error(data?.errorMessage ?? 'Generation failed');
        }

        const updated: FloorplanMessage = {
          ...pendingMsg,
          status: 'success',
          resultingStoragePath: data.resultingPath,
          openaiResponseId: data.newResponseId,
          creditsCharged: 1,
        };
        setMessages((prev) => prev.map((m) => (m.id === pendingMsg.id ? updated : m)));
        // Re-read onResult from ref so we use the latest closure (parent's
        // onResult callback captures `activeVersion` — must reflect what
        // version the user is actually on at send-completion time).
        argsRef.current.onResult?.(updated);
        return updated;
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === pendingMsg.id
              ? { ...m, status: 'failed' as const, errorMessage: errMsg }
              : m
          )
        );
        setError(errMsg);
        throw err;
      } finally {
        setIsGenerating(false);
      }
    },
    // No deps: refs handle freshness. Stable identity reduces parent
    // re-render cascades (handleSend depends on `thread`, which would
    // otherwise rebuild on every render due to args being a new object).
    [],
  );

  const dismissUncertain = useCallback(() => {
    setUncertainHint(null);
    setLastSendArgs(null);
  }, []);

  const dismissRebuildSuggestion = useCallback(() => {
    setRebuildSuggestion(null);
  }, []);

  const forceEditRetry = useCallback(async (): Promise<FloorplanMessage | null> => {
    if (!lastSendArgs) return null;
    setRebuildSuggestion(null);
    const cachedPrompt = lastSendArgs.prompt;
    const cachedBox = lastSendArgs.box;

    // Read latest values via refs (see sendMessage comment for rationale).
    const liveArgs = argsRef.current;
    const liveStatePath = currentStatePathRef.current;

    if (!liveArgs.activeVersion) throw new Error('No active version');
    if (!liveStatePath) throw new Error('No current state path');

    setIsGenerating(true);
    const pendingMsg = await createPendingMessage(liveArgs.activeVersion.id, cachedPrompt, cachedBox);
    setMessages((prev) => [...prev, pendingMsg]);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('generate-floorplan-refinement', {
        body: {
          imageId: liveArgs.imageId,
          versionId: liveArgs.activeVersion.id,
          messageId: pendingMsg.id,
          userPrompt: cachedPrompt,
          boxCoordinates: cachedBox ?? undefined,
          currentStatePath: liveStatePath,
          sourceStoragePath: liveArgs.sourceStoragePath,
          projectId: liveArgs.projectId,
          userId: liveArgs.userId,
          force_edit: true,
        },
      });
      if (fnErr) throw fnErr;
      if (data?.success !== true) throw new Error(data?.errorMessage ?? 'force_edit retry failed');
      const updated: FloorplanMessage = {
        ...pendingMsg,
        status: 'success' as const,
        resultingStoragePath: data.resultingPath,
      };
      setMessages((prev) =>
        prev.map((m) => m.id === pendingMsg.id ? updated : m),
      );
      argsRef.current.onResult?.(updated);
      return updated;
    } finally {
      setIsGenerating(false);
      setLastSendArgs(null);
    }
  }, [lastSendArgs]);

  const hardReset = useCallback(
    async (keepUpToMessageNumber: number): Promise<void> => {
      const liveArgs = argsRef.current;
      if (!liveArgs.activeVersion) return;
      await hardResetFromMessage(liveArgs.activeVersion.id, keepUpToMessageNumber);
      setMessages((prev) => prev.filter((m) => m.messageNumber <= keepUpToMessageNumber));
      await setActivePointer(
        liveArgs.imageId,
        liveArgs.activeVersion.id,
        keepUpToMessageNumber > 0 ? keepUpToMessageNumber : null
      );
    },
    [],
  );

  const setActiveMessage = useCallback(
    async (messageNumber: number | null): Promise<void> => {
      const liveArgs = argsRef.current;
      if (!liveArgs.activeVersion) return;
      await setActivePointer(liveArgs.imageId, liveArgs.activeVersion.id, messageNumber);
    },
    [],
  );

  return {
    messages,
    isGenerating,
    error,
    currentStatePath,
    sendMessage,
    rebuildSuggestion,
    uncertainHint,
    dismissUncertain,
    dismissRebuildSuggestion,
    forceEditRetry,
    hardReset,
    setActiveMessage,
  };
}
